/**
 * OVERVOLT map registry — multiple hand-authored playfields.
 *
 * Each {@link MapDef} carries a 20×12 grid layout: a spawn→Core **path
 * polyline** (orthogonally adjacent tiles) plus an optional **blocked-tile
 * set** (terrain that is unbuildable in addition to the path). The path runs
 * from the spawn tile on the left edge to the Core tile on the right edge.
 *
 * All coordinates here are in *playfield-local* pixel space — the origin is
 * the top-left of the playfield, the HUD strip is NOT included. The renderer
 * adds the HUD offset; the simulation works entirely in this space.
 *
 * The **active map** (selected before a run starts) is exposed through the
 * live-binding exports {@link PATH}, {@link SPAWN_CELL}, {@link CORE_CELL} (and
 * their tile-count siblings). ES-module live bindings mean every consumer sees
 * the latest map automatically after {@link setActiveMap} is called — no need
 * to thread the map through every call site. See docs/GAME_DESIGN.md §3.
 */
import { CONFIG } from '../config';

/** A grid cell coordinate. */
export interface Cell {
  col: number;
  row: number;
}

/** A pixel point in playfield-local space. */
export interface Point {
  x: number;
  y: number;
}

/**
 * A hand-authored map: path polyline + blocked terrain. See docs/GAME_DESIGN.md
 * §3. Multiple maps are registered in {@link MAPS}; one is the active map at a
 * time (see {@link setActiveMap}).
 */
export interface MapDef {
  /** Stable id used by the URL `?map=` param, the run save, and the test API. */
  id: string;
  /** Human-readable name (a chip on the title screen). */
  label: string;
  /** Spawn→Core path polyline; orthogonally adjacent tiles, index 0 is spawn. */
  path: readonly Cell[];
  /**
   * Unbuildable terrain tiles — placement rejects these in addition to the
   * path. A cramped map (Sprint 21) uses this to constrain the buildable
   * space so placement becomes a real puzzle. An empty array (the original
   * `default` map) keeps the classic open-around-the-path layout.
   */
  blocked: readonly Cell[];
  /** One-line tagline for the title-screen chip / docs. Optional. */
  tagline?: string;
}

// --- MAP: default ('Datawash') — the classic open S-bend ---------------------

const DEFAULT_PATH: readonly Cell[] = [
  // Enter left edge along row 6.
  { col: 0, row: 6 }, { col: 1, row: 6 }, { col: 2, row: 6 }, { col: 3, row: 6 }, { col: 4, row: 6 },
  // Climb up column 4.
  { col: 4, row: 5 }, { col: 4, row: 4 }, { col: 4, row: 3 }, { col: 4, row: 2 },
  // Run right along row 2.
  { col: 5, row: 2 }, { col: 6, row: 2 }, { col: 7, row: 2 }, { col: 8, row: 2 }, { col: 9, row: 2 },
  // Drop down column 9.
  { col: 9, row: 3 }, { col: 9, row: 4 }, { col: 9, row: 5 }, { col: 9, row: 6 },
  { col: 9, row: 7 }, { col: 9, row: 8 }, { col: 9, row: 9 },
  // Run right along row 9.
  { col: 10, row: 9 }, { col: 11, row: 9 }, { col: 12, row: 9 }, { col: 13, row: 9 }, { col: 14, row: 9 },
  // Climb up column 14.
  { col: 14, row: 8 }, { col: 14, row: 7 }, { col: 14, row: 6 },
  // Exit right edge along row 6 into the Core.
  { col: 15, row: 6 }, { col: 16, row: 6 }, { col: 17, row: 6 }, { col: 18, row: 6 }, { col: 19, row: 6 },
];

const DEFAULT_MAP: MapDef = {
  id: 'default',
  label: 'Datawash',
  path: DEFAULT_PATH,
  blocked: [],
  tagline: 'Open S-bend — the classic OVERVOLT layout.',
};

// --- MAP: cramped ('Switchyard') — constrained build-space (Sprint 21) -------

/**
 * The path threads four engineered placement situations:
 *  - **Long straight along row 3 (cols 3–11):** 9 tiles in one straight line —
 *    the Sniper Rail's lance lights it up; the Mortar's annulus rides it.
 *  - **Back-deep pocket at the bottom (rows 9–11):** a U-bend that exposes
 *    deep tiles below the path; a Mortar dropped 2+ tiles back hits the U
 *    from inside its annulus.
 *  - **Pinch at (col 13, row 6):** a 1-tile-wide neck framed top + bottom by
 *    terrain walls — a Cryo painter slows the whole wave here and brittles
 *    everything that squeezes through.
 *  - **Final approach along row 6 (cols 14–19):** clean shooting gallery
 *    coming out of the pinch; a brittled enemy stays in damage range.
 *
 * Blocked-tile fraction: ~22% (46 of 206 off-path tiles), with the
 * unbuildable tiles clustered to deny the universal "corner" answer and
 * push the player toward the engineered pockets. See docs/GAME_DESIGN.md §3.
 */
const CRAMPED_PATH: readonly Cell[] = [
  // Enter left edge along row 6, then drop down to the bottom.
  { col: 0, row: 6 }, { col: 1, row: 6 }, { col: 2, row: 6 },
  { col: 2, row: 7 }, { col: 2, row: 8 }, { col: 2, row: 9 }, { col: 2, row: 10 },
  // Bottom run rightward across rows 10 → 11 (the back-deep U).
  { col: 3, row: 10 }, { col: 4, row: 10 }, { col: 5, row: 10 },
  { col: 5, row: 9 }, { col: 5, row: 8 }, { col: 5, row: 7 },
  { col: 5, row: 6 }, { col: 5, row: 5 }, { col: 5, row: 4 }, { col: 5, row: 3 },
  // Long straight along row 3 (cols 5 → 11) — Sniper / Mortar lane.
  { col: 6, row: 3 }, { col: 7, row: 3 }, { col: 8, row: 3 }, { col: 9, row: 3 },
  { col: 10, row: 3 }, { col: 11, row: 3 },
  // Drop into the central pinch.
  { col: 11, row: 4 }, { col: 11, row: 5 }, { col: 11, row: 6 },
  // Squeeze through the choke at col 12-13, row 6 (the painter pinch).
  { col: 12, row: 6 }, { col: 13, row: 6 },
  // Final approach along row 6 to the Core.
  { col: 14, row: 6 }, { col: 15, row: 6 }, { col: 16, row: 6 }, { col: 17, row: 6 },
  { col: 18, row: 6 }, { col: 19, row: 6 },
];

/**
 * Unbuildable terrain — frames the engineered placement situations:
 *  - Top-row band (rows 0–1, cols 0–11) caps the long straight from above:
 *    no over-the-top corner camping.
 *  - Inside-U fill (cols 3–4, rows 5–9) closes the bottom corner so the
 *    back-deep tiles must be earned outside the U, not above it.
 *  - Pinch frame (col 12–13 rows 4–5 and rows 7–8) makes the choke a true
 *    one-tile neck.
 *  - Right-edge band (cols 17–19 rows 9–11) denies the right corner.
 */
const CRAMPED_BLOCKED: readonly Cell[] = [
  // Top band — caps the upper half of the field.
  { col: 0, row: 0 }, { col: 1, row: 0 }, { col: 2, row: 0 }, { col: 3, row: 0 },
  { col: 4, row: 0 }, { col: 5, row: 0 }, { col: 6, row: 0 }, { col: 7, row: 0 },
  { col: 8, row: 0 }, { col: 9, row: 0 }, { col: 10, row: 0 }, { col: 11, row: 0 },
  { col: 0, row: 1 }, { col: 1, row: 1 }, { col: 8, row: 1 },
  // Inside the bottom U — closes the back-pocket from above.
  { col: 3, row: 8 }, { col: 4, row: 8 },
  { col: 3, row: 9 }, { col: 4, row: 9 },
  // Pinch frame at col 12-13 around row 6 (rows 5, 7, 8 walled).
  { col: 12, row: 4 }, { col: 12, row: 5 },
  { col: 13, row: 4 }, { col: 13, row: 5 },
  { col: 12, row: 7 }, { col: 12, row: 8 },
  { col: 13, row: 7 }, { col: 13, row: 8 },
  // Right-edge band — denies the right corner.
  { col: 17, row: 10 }, { col: 18, row: 10 }, { col: 19, row: 10 },
  { col: 17, row: 11 }, { col: 18, row: 11 }, { col: 19, row: 11 },
  // Scattered interior obstacles — break large open pockets.
  { col: 0, row: 3 }, { col: 0, row: 4 },
  { col: 7, row: 5 }, { col: 8, row: 5 }, { col: 9, row: 5 },
  { col: 7, row: 8 }, { col: 8, row: 8 }, { col: 9, row: 8 },
  { col: 15, row: 3 }, { col: 16, row: 3 }, { col: 17, row: 3 },
  { col: 15, row: 9 }, { col: 16, row: 9 },
];

const CRAMPED_MAP: MapDef = {
  id: 'cramped',
  label: 'Switchyard',
  path: CRAMPED_PATH,
  blocked: CRAMPED_BLOCKED,
  tagline: 'Cramped lanes — every tile is contested.',
};

// --- MAP: lines ('Long Lines') — long-straights axis (Sprint 37) -------------

/**
 * The second hand-authored map (Sprint 37, REC 4 follow-on). Where Switchyard
 * pushes on **buildable-tile scarcity** (`docs/STRATEGY_ANALYSIS.md` §6
 * property 3), Long Lines pushes on **straight-vs-serpentine ratio** (§6
 * property 2): two stacked **8-tile vertical straights** at cols 4 and 9
 * cash in the Sprint-13 Sniper Rail's aligned 1.68× corner and give the
 * Sprint-31 Streak its natural lane.
 *
 * The path threads three engineered placement situations:
 *  - **Col-4 straight (rows 0–7):** 8 contiguous tiles in column 4 — a Sniper
 *    on the same column lights up the whole straight in one shot. The early
 *    straight catches the wave still fresh.
 *  - **Single-elbow connector (row 0, cols 5–8):** the only deviation between
 *    the two straights — 4 horizontal tiles at the top edge. Mortar dropped
 *    back from row 0 hits the elbow from inside its annulus.
 *  - **Col-9 straight (rows 0–7):** 8 contiguous tiles in column 9 — the second
 *    aligned Sniper lane, plus the natural Cryo painter slot to brittle the
 *    wave before the lead-out gauntlet. The Streak's lone-wolf sprint runs
 *    here unless paired with a slower escort.
 *
 * Path length: **36 tiles** (vs. the default's 34). Two 8-tile vertical
 * straights consume 14 vertical tile-deltas exactly; the row-6 canonical
 * endpoints force the lead-in / lead-out to add 2 more tile-deltas (one
 * row-7 dip in, one row-7 dip out), yielding a 36-tile path. This is the
 * minimum feasible for the requirement and sits well within the §3.3
 * "± a few" tolerance for §9 bounty-decay calibration.
 *
 * Blocked-tile fraction: **~8%** (17 of 204 off-path tiles) — the LIGHT
 * contrast Switchyard's ~22% asks for. The blocked tiles deny only the
 * extreme corner pile-ups; the natural Sniper alignment columns (cols 4 and
 * 9 below row 7) are deliberately left clear.
 */
const LINES_PATH: readonly Cell[] = [
  // Lead-in: row 6 to col 2, then drop to row 7 to feed the first straight.
  { col: 0, row: 6 }, { col: 1, row: 6 }, { col: 2, row: 6 },
  { col: 2, row: 7 }, { col: 3, row: 7 }, { col: 4, row: 7 },
  // Long straight #1 — col 4, row 7 → row 0 (8 contiguous col-4 tiles).
  { col: 4, row: 6 }, { col: 4, row: 5 }, { col: 4, row: 4 },
  { col: 4, row: 3 }, { col: 4, row: 2 }, { col: 4, row: 1 },
  { col: 4, row: 0 },
  // Single elbow connector at row 0, cols 5 → 8 (4 tiles).
  { col: 5, row: 0 }, { col: 6, row: 0 }, { col: 7, row: 0 }, { col: 8, row: 0 },
  // Long straight #2 — col 9, row 0 → row 7 (8 contiguous col-9 tiles).
  { col: 9, row: 0 }, { col: 9, row: 1 }, { col: 9, row: 2 },
  { col: 9, row: 3 }, { col: 9, row: 4 }, { col: 9, row: 5 },
  { col: 9, row: 6 }, { col: 9, row: 7 },
  // Lead-out: row 7 right to col 19, then up into the Core.
  { col: 10, row: 7 }, { col: 11, row: 7 }, { col: 12, row: 7 }, { col: 13, row: 7 },
  { col: 14, row: 7 }, { col: 15, row: 7 }, { col: 16, row: 7 }, { col: 17, row: 7 },
  { col: 18, row: 7 }, { col: 19, row: 7 },
  { col: 19, row: 6 },
];

/**
 * Unbuildable terrain — a LIGHT scatter that denies the absolute corner
 * pile-ups without closing off the natural Sniper-alignment lanes. Cols 4
 * and 9 below row 7 are deliberately left clear so a Sniper can sit on the
 * straight's lower extension and skewer the whole column.
 *
 *  - Top-left and top-right corner caps (4 tiles) — denies the over-the-top
 *    Sniper camp where the straight would reduce to a single grazing hit.
 *  - Bottom-left and bottom-right corner caps (4 tiles) — symmetric denial
 *    at the lead-out side.
 *  - Mid-board scatter on row 5 (4 tiles) — cols 5–8: breaks the open
 *    pocket beneath the elbow so a "throw down 4 Pulses below the elbow"
 *    answer is the WRONG answer; pushes the player back onto the straights.
 *  - Bottom-edge scatter (3 tiles) — single tiles in row 10/11 to keep
 *    bottom-lane Sniper placement a deliberate choice, not a default.
 *  - Right-side scatter (2 tiles) — caps the over-Core lane camp.
 */
const LINES_BLOCKED: readonly Cell[] = [
  // Top-left and top-right corner caps.
  { col: 0, row: 0 }, { col: 0, row: 1 },
  { col: 19, row: 0 }, { col: 19, row: 1 },
  // Bottom-left and bottom-right corner caps.
  { col: 0, row: 11 }, { col: 1, row: 11 },
  { col: 18, row: 11 }, { col: 19, row: 11 },
  // Mid-board row-5 scatter beneath the elbow (cols 5–8).
  { col: 5, row: 5 }, { col: 6, row: 5 }, { col: 7, row: 5 }, { col: 8, row: 5 },
  // Bottom-edge scatter — single tiles, no clustered camps.
  { col: 6, row: 11 }, { col: 12, row: 11 },
  { col: 15, row: 10 },
  // Right-side scatter.
  { col: 19, row: 2 }, { col: 19, row: 10 },
];

const LINES_MAP: MapDef = {
  id: 'lines',
  label: 'Long Lines',
  path: LINES_PATH,
  blocked: LINES_BLOCKED,
  tagline: 'Long straights — lane the lance.',
};

// --- MAP: serpentine ('Snake Run') — tight-serpentine (Sprint 43, map3-s43) --

/**
 * The third hand-authored map (Sprint 43, REC 4 follow-on). Where Switchyard
 * pushed **buildable-tile scarcity** (`docs/STRATEGY_ANALYSIS.md` §6 property
 * 3) and Long Lines pushed **straight-vs-serpentine ratio** (§6 property 2),
 * Snake Run pushes on **chokepoint count** (§6 property 4) and the
 * tight-serpentine axis named in `docs/PLAYTEST_KEYSTONE.md` §4: many short
 * bends, no long straights, lots of pinches. The map deliberately
 * disadvantages the §5 Sniper Rail (no alignment payoff) and the Mortar
 * (few back-deep tiles) while letting the §5 Cryo painter shine — the
 * tight bends create off-path **3-cover spots** where a single Cryo at the
 * inside of a W-bend paints THREE path tiles at once.
 *
 * The path threads four engineered placement situations:
 *  - **Tight 7-wiggle serpentine (cols 0–19):** the path bends every 1–2
 *    tiles, alternating rows 5 and 6 in a zig-zag with no straight run
 *    longer than 3 path tiles. The Sniper's line-of-fire pierce lance
 *    finds barely 2 path tiles at any aligned firing line.
 *  - **Four Cryo pinches at (3,5) / (9,5) / (12,5) / (15,5):** each off-path
 *    row-5 tile sits at the inside of a W-bend, with three orthogonally
 *    adjacent path tiles. A Cryo painter dropped on a pinch paints 3
 *    enemies on EACH pulse — vs. 1 at a non-pinch placement. (A fifth
 *    bonus pinch sits at (6,5); the test picks one of the four named ones.)
 *  - **Tight band of buildable space (rows 4 and 7–9):** the row-4 and
 *    row-8 blocked-tile bands frame the pinches and deny the deepest
 *    Mortar lanes. The annulus has few legal anchors that can reach
 *    multiple bends — Mortar's 2.0–3.6 ring lands awkwardly on a path
 *    that turns inside the ring.
 *  - **No long row-aligned run anywhere:** every same-row contiguous run
 *    is ≤ 3 path tiles; combined with the row-4/row-8 blocked band, the
 *    Sniper Rail's 1.68× corner uplift evaporates. The map's question is
 *    "which TWO of the four pinches do I paint?"
 *
 * Path length: **34 tiles** (exact match to the §8 economy — same as
 * Switchyard and the default S-bend). Endpoints at the canonical
 * `(0,6) → (19,6)` row-6 entry/exit.
 *
 * Blocked-tile fraction: **~11%** (23 of 206 off-path tiles) — the
 * "medium contrast" Switchyard's ~22% / Long Lines' ~8% asks for.
 * The blocked tiles cluster into a row-4 "ceiling" above the pinches
 * and a sparser row-8 "floor" beneath the bends, with the four corners
 * capped so a corner-cluster build cannot answer.
 */
const SERPENTINE_PATH: readonly Cell[] = [
  // Enter left edge along row 6, then climb into the first wiggle.
  { col: 0, row: 6 }, { col: 1, row: 6 },
  // Wiggle 1 — apex row 5 (cols 1-2).
  { col: 1, row: 5 }, { col: 2, row: 5 },
  { col: 2, row: 6 }, { col: 3, row: 6 },
  // Wiggle 2 — apex row 5 (cols 4-5); pinch P1 at (3,5).
  { col: 4, row: 6 }, { col: 4, row: 5 }, { col: 5, row: 5 },
  { col: 5, row: 6 }, { col: 6, row: 6 },
  // Wiggle 3 — apex row 5 (cols 7-8); pinch P2 at (6,5).
  { col: 7, row: 6 }, { col: 7, row: 5 }, { col: 8, row: 5 },
  { col: 8, row: 6 }, { col: 9, row: 6 },
  // Wiggle 4 — apex row 5 (cols 10-11); pinch P3 at (9,5).
  { col: 10, row: 6 }, { col: 10, row: 5 }, { col: 11, row: 5 },
  { col: 11, row: 6 }, { col: 12, row: 6 },
  // Wiggle 5 — apex row 5 (cols 13-14); pinch P4 at (12,5).
  { col: 13, row: 6 }, { col: 13, row: 5 }, { col: 14, row: 5 },
  { col: 14, row: 6 }, { col: 15, row: 6 },
  // Wiggle 6 — apex row 5 (cols 16-17); pinch P5 at (15,5).
  { col: 16, row: 6 }, { col: 16, row: 5 }, { col: 17, row: 5 },
  { col: 17, row: 6 },
  // Wiggle 7 — apex row 5 (cols 18-19); exit at Core (19,6).
  { col: 18, row: 6 }, { col: 18, row: 5 }, { col: 19, row: 5 },
  { col: 19, row: 6 },
];

/**
 * Unbuildable terrain — the "medium-contrast" Switchyard / Long Lines mid-
 * point. The blocked tiles cluster into three bands:
 *
 *  - **Row-4 ceiling above the pinches** (5 tiles): `(3,4) (6,4) (9,4)
 *    (12,4) (15,4)` — these sit DIRECTLY above the five W-bend pinch
 *    tiles. The visual cue: each off-path row-5 pinch is framed by a
 *    blocked tile above AND a single legal Cryo footprint. Denies the
 *    "drop a Mortar at row 2/3 above a pinch" answer.
 *  - **Row-8 floor below the bends** (5 tiles): `(3,8) (6,8) (9,8) (12,8)
 *    (15,8)` — same column positions, row 8. Symmetric framing; denies
 *    a Mortar parked below the wave's path.
 *  - **Four corners capped** (12 tiles): `(0,0)(1,0)(0,1)(18,0)(19,0)
 *    (19,1)` top, `(0,10)(0,11)(1,11)(18,11)(19,10)(19,11)` bottom — the
 *    universal "corner cluster" Pulse / Arc answer cannot land here.
 *  - **Single (0,5) cap** (1 tile): denies a Sniper parked at the spawn-
 *    column shoulder; keeps the Sniper test placements honest.
 *
 * Total: 23 blocked tiles ≈ 11.2% of the 206 off-path tiles. The
 * five pinches `(3,5) (6,5) (9,5) (12,5) (15,5)` are deliberately LEFT
 * buildable — Cryo's natural seat.
 */
const SERPENTINE_BLOCKED: readonly Cell[] = [
  // Row-4 ceiling above the pinches.
  { col: 3, row: 4 }, { col: 6, row: 4 }, { col: 9, row: 4 },
  { col: 12, row: 4 }, { col: 15, row: 4 },
  // Row-8 floor below the bends.
  { col: 3, row: 8 }, { col: 6, row: 8 }, { col: 9, row: 8 },
  { col: 12, row: 8 }, { col: 15, row: 8 },
  // Top corner caps — denies upper-corner cluster builds.
  { col: 0, row: 0 }, { col: 1, row: 0 }, { col: 0, row: 1 },
  { col: 18, row: 0 }, { col: 19, row: 0 }, { col: 19, row: 1 },
  // Bottom corner caps — denies lower-corner cluster builds.
  { col: 0, row: 10 }, { col: 0, row: 11 }, { col: 1, row: 11 },
  { col: 19, row: 10 }, { col: 18, row: 11 }, { col: 19, row: 11 },
  // Spawn-shoulder cap — denies a shallow-angle Sniper on the entry side.
  { col: 0, row: 5 },
];

const SERPENTINE_MAP: MapDef = {
  id: 'serpentine',
  label: 'Snake Run',
  path: SERPENTINE_PATH,
  blocked: SERPENTINE_BLOCKED,
  tagline: 'Tight serpentine — Cryo painter shines.',
};

// --- registry + active map ---------------------------------------------------

/**
 * Map registry — id → {@link MapDef}. Add a new map here AND list its id in
 * `CONFIG.mapIds` (src/config.ts map registry section) so the title-screen
 * cycle picks it up. The selection wiring (URL `?map=<id>`, the title-screen
 * chip, `__GAME__.setMap(id)`) consults this single source of truth.
 */
export const MAPS: Record<string, MapDef> = {
  default: DEFAULT_MAP,
  cramped: CRAMPED_MAP,
  lines: LINES_MAP,
  serpentine: SERPENTINE_MAP,
};

/** The bootstrap default — the map a fresh run lands on with no URL override. */
export const DEFAULT_MAP_ID = 'default';

let _active: MapDef = DEFAULT_MAP;
let _pathKeys: Set<number> = new Set();
let _blockedKeys: Set<number> = new Set();

/** Pack (col, row) into a single integer key for fast Set lookup. */
function packKey(col: number, row: number): number {
  return row * CONFIG.grid.cols + col;
}

/** Rebuild the path / blocked tile lookup sets after an active-map change. */
function rebuildLookups(): void {
  _pathKeys = new Set(_active.path.map((c) => packKey(c.col, c.row)));
  _blockedKeys = new Set(_active.blocked.map((c) => packKey(c.col, c.row)));
}
rebuildLookups();

/**
 * The path as an ordered list of grid cells. Consecutive cells are always
 * orthogonally adjacent. Index 0 is the spawn tile, the last index is the
 * Core tile. **Live binding** — switching the active map via
 * {@link setActiveMap} updates this for every importing module.
 */
export let PATH: readonly Cell[] = _active.path;

/** Total path tiles on the active map. Live binding (see {@link PATH}). */
export let PATH_TILE_COUNT = _active.path.length;

/**
 * Distance (in tiles) an enemy must travel from spawn to reach the Core on the
 * active map (one less than {@link PATH_TILE_COUNT}). Live binding.
 */
export let PATH_LENGTH = _active.path.length - 1;

/** The active map's spawn tile (enemies appear here). Live binding. */
export let SPAWN_CELL: Cell = _active.path[0]!;

/** The active map's Core tile (enemies leak here; the player defends it). Live binding. */
export let CORE_CELL: Cell = _active.path[_active.path.length - 1]!;

/** Read the active map record. */
export function activeMap(): MapDef {
  return _active;
}

/** Read the active map id — mirrored into the run save / snapshot. */
export function activeMapId(): string {
  return _active.id;
}

/** Look up a map by id; returns `undefined` for an unknown id. */
export function getMap(id: string): MapDef | undefined {
  return MAPS[id];
}

/**
 * Switch the active map. The exported {@link PATH} / {@link SPAWN_CELL} /
 * {@link CORE_CELL} bindings update across every importing module
 * automatically (ES module live bindings), and the path / blocked lookups
 * for {@link isPathCell} / {@link isBlockedTile} are rebuilt in lockstep.
 * Unknown ids fall back to the bootstrap default. Returns the resolved map.
 *
 * Must be called BEFORE `createInitialState` / `recomputePaths` runs so the
 * fresh state's path snaps to the chosen map; the orchestrator does this in
 * `boot` / `reset` and again on resume.
 */
export function setActiveMap(id: string): MapDef {
  const m = MAPS[id] ?? DEFAULT_MAP;
  _active = m;
  PATH = m.path;
  PATH_TILE_COUNT = m.path.length;
  PATH_LENGTH = m.path.length - 1;
  SPAWN_CELL = m.path[0]!;
  CORE_CELL = m.path[m.path.length - 1]!;
  rebuildLookups();
  return m;
}

// --- queries -----------------------------------------------------------------

/** The pixel center of a grid cell, in playfield-local space. */
export function cellCenter(col: number, row: number): Point {
  const t = CONFIG.grid.tileSize;
  return { x: col * t + t / 2, y: row * t + t / 2 };
}

/** True when (col, row) lies on the active map's enemy path. */
export function isPathCell(col: number, row: number): boolean {
  return _pathKeys.has(packKey(col, row));
}

/**
 * True when (col, row) is a permanently-unbuildable terrain tile on the
 * active map — the Sprint 21 cramped-map mechanic. A blocked tile rejects
 * `placeTower` / `canPlace`; the renderer paints it as solid terrain so the
 * player reads the constraint at a glance.
 */
export function isBlockedTile(col: number, row: number): boolean {
  return _blockedKeys.has(packKey(col, row));
}

/** True when (col, row) is inside the grid bounds. */
export function inBounds(col: number, row: number): boolean {
  return col >= 0 && row >= 0 && col < CONFIG.grid.cols && row < CONFIG.grid.rows;
}

/**
 * True when a tower may be built on (col, row) — in bounds, not on the path,
 * and not a blocked terrain tile. (Occupancy by an existing tower is checked
 * separately by the caller.)
 */
export function isBuildable(col: number, row: number): boolean {
  return inBounds(col, row) && !isPathCell(col, row) && !isBlockedTile(col, row);
}

/**
 * True when (col, row) is the spawn tile or the Core tile — the two permanent
 * walkable endpoints. Used by Maze-mode placement validation (§16.5 rule 1);
 * on a fixed-path map both already lie on the path.
 */
export function isEndpoint(col: number, row: number): boolean {
  return (
    (col === SPAWN_CELL.col && row === SPAWN_CELL.row) ||
    (col === CORE_CELL.col && row === CORE_CELL.row)
  );
}

/**
 * Interpolated pixel position at `dist` tiles along an arbitrary tile polyline,
 * in playfield-local space. `dist` is clamped to `[0, path.length − 1]`. This
 * is the shared interpolator for both the fixed map and every Maze-mode A*
 * route (a per-enemy polyline) — see docs/GAME_DESIGN.md §16.6.
 */
export function pointAtPath(path: readonly Cell[], dist: number): Point {
  const maxDist = path.length - 1;
  const clamped = dist < 0 ? 0 : dist > maxDist ? maxDist : dist;
  const i = Math.floor(clamped);
  if (i >= maxDist) {
    const last = path[path.length - 1]!;
    return cellCenter(last.col, last.row);
  }
  const a = path[i]!;
  const b = path[i + 1]!;
  const f = clamped - i;
  const ca = cellCenter(a.col, a.row);
  const cb = cellCenter(b.col, b.row);
  return { x: ca.x + (cb.x - ca.x) * f, y: ca.y + (cb.y - ca.y) * f };
}

/**
 * Interpolated pixel position at `dist` tiles along the active fixed path, in
 * playfield-local space. A thin wrapper over {@link pointAtPath} for the
 * Campaign / Endless map.
 */
export function pathPointAt(dist: number): Point {
  return pointAtPath(PATH, dist);
}

/** Convert a playfield-local pixel coordinate to a grid cell. */
export function pointToCell(x: number, y: number): Cell {
  const t = CONFIG.grid.tileSize;
  return { col: Math.floor(x / t), row: Math.floor(y / t) };
}
